import { Box, Card, Chip, Group, Stack, Text, ThemeIcon, type MantineColor } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconInbox } from '@tabler/icons-react';
import { Children, type ReactNode } from 'react';
import { Link } from 'react-router';

/** Karta sekcji: tytuł (z ikoną), akcje po prawej, treść. */
export function SectionCard({ title, icon, actions, children, padding = 'lg', footer }: { title?: ReactNode; icon?: ReactNode; actions?: ReactNode; children: ReactNode; padding?: string | number; footer?: ReactNode }) {
  return (
    <Card padding={0}>
      {(title || actions) && (
        <Group justify="space-between" px="lg" pt="md" pb={title ? 'sm' : 0} wrap="nowrap" gap="sm">
          <Group gap="xs" wrap="nowrap">
            {icon && (
              <ThemeIcon variant="light" size="md" radius="md">
                {icon}
              </ThemeIcon>
            )}
            {typeof title === 'string' ? (
              <Text fw={700} size="sm">
                {title}
              </Text>
            ) : (
              title
            )}
          </Group>
          {actions}
        </Group>
      )}
      <Card.Section inheritPadding={false} px={padding} pb={padding} pt={title ? 0 : padding} withBorder={false}>
        {children}
      </Card.Section>
      {footer && (
        <Card.Section withBorder px="lg" py="sm" style={{ background: 'var(--app-muted)' }}>
          {footer}
        </Card.Section>
      )}
    </Card>
  );
}

/** Kafelek z liczbą (pulpit, podsumowania). Z linkiem — cały kafelek jest klikalny. */
export function StatCard({ label, value, icon, color = 'eko', hint, to, tone }: { label: string; value: ReactNode; icon: ReactNode; color?: MantineColor; hint?: ReactNode; to?: string; tone?: 'alert' }) {
  const body = (
    <Card padding="lg" className={to ? 'card-link' : undefined} h="100%" style={{ containerType: 'inline-size', ...(tone === 'alert' ? { borderColor: 'var(--mantine-color-red-3)' } : {}) }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" c="dimmed" fw={500} lh={1.3}>
            {label}
          </Text>
          {/* kwota w jednej linii — czcionka dopasowana do szerokości kafelka (skalowanie Windows, wąskie okna) */}
          <Text fw={750} lh={1.1} className="tabular" c={tone === 'alert' ? 'red.7' : undefined} style={{ fontSize: 'clamp(18px, 10.5cqi, 30px)', whiteSpace: 'nowrap' }}>
            {value}
          </Text>
          {hint && (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          )}
        </Stack>
        <ThemeIcon size={42} radius="md" variant="light" color={tone === 'alert' ? 'red' : color} style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {body}
    </Link>
  ) : (
    body
  );
}

/**
 * Siatka kafelków z liczbami: liczba kolumn dobrana do szerokości (kafelek ≥ ~250 px) tak, żeby rzędy były
 * pełne — np. 6 kafelków: 6, 3 albo 2 kolumny (nigdy 4 + 2 z pustym miejscem). Działa przy skalowaniu Windows.
 */
export function StatGrid({ children, minWidth = 250 }: { children: ReactNode; minWidth?: number }) {
  const { ref, width } = useElementSize();
  const items = Children.toArray(children).filter(Boolean);
  const n = items.length;
  const gap = 16;
  // kandydaci: wszystkie w jednym rzędzie, potem dzielniki (pełne rzędy), na końcu połowa (gdy n nie ma dzielników)
  const options = [n, ...Array.from({ length: Math.max(0, n - 1) }, (_, i) => n - 1 - i).filter((c) => c > 1 && n % c === 0)];
  if (options.length === 1 && n > 3) options.push(Math.ceil(n / 2));
  options.push(1);
  const cols = width ? (options.find((c) => (width - gap * (c - 1)) / c >= minWidth) ?? 1) : Math.min(n, 2);
  return (
    <Box ref={ref} style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap }}>
      {items}
    </Box>
  );
}

/** Pusty stan: ikona, krótki opis, podpowiedź i (opcjonalnie) akcja. */
export function EmptyState({ title, hint, action, icon, compact }: { title: string; hint?: ReactNode; action?: ReactNode; icon?: ReactNode; compact?: boolean }) {
  return (
    <Stack align="center" gap={6} py={compact ? 'md' : 'xl'} px="md" ta="center">
      <ThemeIcon size={compact ? 36 : 48} radius="xl" variant="light" color="gray">
        {icon ?? <IconInbox size={compact ? 18 : 24} />}
      </ThemeIcon>
      <Text fw={600} size={compact ? 'sm' : 'md'}>
        {title}
      </Text>
      {hint && (
        <Text size="sm" c="dimmed" maw={420}>
          {hint}
        </Text>
      )}
      {action}
    </Stack>
  );
}

/** Kropka stanu z etykietą (lżejsza niż znacznik). */
export function StatusDot({ color, label }: { color: MantineColor; label: ReactNode }) {
  return (
    <Group gap={6} wrap="nowrap">
      <span style={{ width: 8, height: 8, borderRadius: 99, background: `var(--mantine-color-${color}-6)`, flexShrink: 0 }} />
      <Text size="sm">{label}</Text>
    </Group>
  );
}

/** Komunikat przy przyciskach, które są nieaktywne z powodu niezapisanych zmian. */
export function UnsavedHint({ show, action }: { show: boolean; action: string }) {
  if (!show) return null;
  return (
    <Text size="sm" c="var(--app-text-orange)" fw={500}>
      Niezapisane zmiany — zapisz, aby {action}
    </Text>
  );
}

/**
 * Filtr listy (widoki: „Otwarte / Po terminie / Wszystkie”…): chipy zawijają się na telefonie,
 * więc nic nie ucieka poza ekran. Jedyny wzór filtrów list w web — zamiast `SegmentedControl`.
 */
export function FilterChips<T extends string>({ value, onChange, options, size = 'sm' }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; color?: MantineColor }[]; size?: 'xs' | 'sm' }) {
  return (
    <Chip.Group value={value} onChange={(v) => onChange(v as T)}>
      <Group gap={6} wrap="wrap">
        {options.map((o) => (
          <Chip key={o.value} value={o.value} size={size} color={o.color}>
            {o.label}
          </Chip>
        ))}
      </Group>
    </Chip.Group>
  );
}
